// Web treatment for the letter-first hook screen (LetterHookScreen.tsx).
//
// On a phone the pitch fills the screen (`content: { flex: 1 }`) and the two
// buttons sit in a footer at the bottom edge, under the thumb. In a tablet
// or desktop column the screen is a viewport tall, so the footer sank to the
// bottom of the window, left a void under the copy, stretched the primary
// button across the whole column and ended up under the docked Orion button.
//
// On those frames the pitch takes only the height it needs and the footer
// follows it, capped at a comfortable width. The phone frame (< 768px) keeps
// the pinned layout, as a device would. Applied inside the `react-native`
// shim's `StyleSheet.create`, like lockUnlockStyle.ts, so the screen file
// does not change and mobile never sees it.
//
// Matched by style-key signature only: a sheet with `topSection`, a
// `content` that flexes, `footer`, `startButton` and `notNowButton`.

import { layoutModeFor } from './useLayoutMode';

type Style = Record<string, unknown>;

/** Widest the two buttons get in a wide column. */
export const LETTER_HOOK_FOOTER_MAX_WIDTH = 560;

function isLetterHookSheet(styles: Record<string, unknown>): boolean {
  const content = styles.content as Style | undefined;
  return (
    !!content &&
    typeof content === 'object' &&
    content.flex === 1 &&
    'topSection' in styles &&
    'footer' in styles &&
    'startButton' in styles &&
    'notNowButton' in styles
  );
}

function widthNow(): number {
  return typeof window === 'undefined' ? 0 : window.innerWidth;
}

export function relayoutLetterHook<T extends Record<string, unknown>>(
  styles: T,
  viewportWidth: number = widthNow(),
): T {
  if (!isLetterHookSheet(styles)) return styles;
  if (layoutModeFor(viewportWidth) === 'phone') return styles;

  const out: Record<string, unknown> = { ...styles };
  const { flex: _flex, ...content } = styles.content as Style;
  void _flex;
  out.content = { ...content, flexGrow: 0, flexShrink: 0, flexBasis: 'auto' };
  out.footer = {
    ...(styles.footer as Style),
    marginTop: 32,
    width: '100%',
    maxWidth: LETTER_HOOK_FOOTER_MAX_WIDTH,
    alignSelf: 'center',
  };
  return out as T;
}
